// The Snitcher tracker (window.Snitcher) is our own product's tracking script,
// dogfooded on the app itself — resources/views/tracking/snitcher.blade.php
// boots it, and it is present only for the Snitcher brand, outside
// impersonation, once the snippet has rendered. This module owns the two
// session-level calls the tracker needs to attribute product usage — identify
// (who is using the app) and company (which workspace they belong to) — plus
// the `product.` namespace every in-product `.track` event carries, mirroring
// the `website.` namespace the marketing site's tracker stamps on visitor
// telemetry so a reader can tell the two apart by event name alone.
//
// PostHog is deliberately untouched here: the company payload below is the
// FE-safe subset of the workspace attributes we already sync to PostHog
// server-side. Anything sensitive — revenue (MRR/ARR), ICP/firmographic
// enrichment, spam or staff/internal flags, cross-tenant data — stays in the
// server-side sync and must never appear in this browser-visible payload.

export const PRODUCT_EVENT_PREFIX = 'product.'

export function productEventName(event: string): string {
    return event.startsWith(PRODUCT_EVENT_PREFIX)
        ? event
        : `${PRODUCT_EVENT_PREFIX}${event}`
}

// Minimal shape of the shared Inertia `user` prop this module reads. Kept local
// and loose so the tracker stays decoupled from the full store User type (same
// approach as lib/posthog.ts's PostHogUser).
export interface SnitcherTrackerUser {
    id?: number | string
    email?: string
    name?: string
    current_site?: {
        domain?: string | null
        name?: string | null
        plan?: string | null
        subscription_status?: string | null
    } | null
}

// The identity and company we last pushed, so a same-user / same-company Inertia
// navigation does not re-fire the calls on every page change. The company key
// covers the full payload, not just the domain, so a mid-session trait change
// (plan upgrade, past-due transition) re-fires with the fresh traits.
let identifiedEmail: string | null = null
let lastCompanyKey: string | null = null

// Establish the session's company and person on the Snitcher tracker. Company is
// keyed by the workspace's own domain — the same identifier the tracker uses for
// any visiting company — and carries only the FE-safe company traits. Called at
// app boot and on every Inertia navigation; guarded to fire each call once per
// real change.
export function identifySnitcherSession(
    user: SnitcherTrackerUser | null | undefined,
    options: { isImpersonating?: boolean } = {}
): void {
    if (options.isImpersonating) {
        return
    }

    const snitcher = window.Snitcher
    if (!snitcher || !user?.email) {
        return
    }

    const site = user.current_site ?? null
    const domain = site?.domain ?? null
    const companyKey = domain
        ? JSON.stringify([
              domain,
              site?.name,
              site?.plan,
              site?.subscription_status,
          ])
        : null

    if (domain && companyKey !== lastCompanyKey) {
        snitcher.company?.(domain, {
            name: site?.name ?? undefined,
            plan: site?.plan ?? undefined,
            subscription_status: site?.subscription_status ?? undefined,
        })
        lastCompanyKey = companyKey
    }

    if (user.email !== identifiedEmail) {
        snitcher.identify?.(user.email, {
            name: user.name,
            user_id: user.id,
        })
        identifiedEmail = user.email
    }
}

// Clear the pushed identity/company so the next session (logout, user switch in
// a shared browser) re-fires, and tell the tracker to drop the identity it
// persisted so post-logout events are not attributed to the previous user.
// A full reload re-renders the snippet fresh, so this only matters for the
// in-SPA switch.
export function resetSnitcherSession(): void {
    identifiedEmail = null
    lastCompanyKey = null
    window.Snitcher?.reset?.()
}

// Track an in-product event through the Snitcher tracker, namespaced `product.`.
// No-ops when the tracker is absent (dev, logged-out, whitelabel); every call
// site already relied on that optional-chaining guard.
export function trackProduct(
    event: string,
    properties?: Record<string, unknown>
): void {
    window.Snitcher?.track?.(productEventName(event), properties)
}
